import { useMemo } from 'react'
import { formatNumber } from 'src/utils/functions'

export interface AggregateValuesPlotProps {
  data:
    | {
        '<25': number
        '25-100': number
        '100-500': number
        '>500': number
      }
    | {
        '<5': number
        '5-25': number
        '25-100': number
        '100-500': number
        '>500': number
      }
}

export default function AggregateValuesPlot(props: AggregateValuesPlotProps) {
  const { data: propsData } = props

  const data = useMemo(() => {
    const totalSum = Object.values(propsData).reduce(
      (acc, curr) => acc + curr,
      0
    )
    const result: {
      label: string
      value: number
      percentage: number
    }[] = []
    if ('<5' in propsData) {
      result.push({
        label: '<5',
        value: propsData['<5'],
        percentage: (propsData['<5'] / totalSum) * 100
      })
    }
    if ('5-25' in propsData) {
      result.push({
        label: '5-25',
        value: propsData['5-25'],
        percentage: (propsData['5-25'] / totalSum) * 100
      })
    }
    if ('<25' in propsData) {
      result.push({
        label: '<25',
        value: propsData['<25'],
        percentage: (propsData['<25'] / totalSum) * 100
      })
    }
    if ('25-100' in propsData) {
      result.push({
        label: '25-100',
        value: propsData['25-100'],
        percentage: (propsData['25-100'] / totalSum) * 100
      })
    }
    if ('100-500' in propsData) {
      result.push({
        label: '100-500',
        value: propsData['100-500'],
        percentage: (propsData['100-500'] / totalSum) * 100
      })
    }
    if ('>500' in propsData) {
      result.push({
        label: '>500',
        value: propsData['>500'],
        percentage: (propsData['>500'] / totalSum) * 100
      })
    }
    return result
  }, [propsData])

  return (
    <div className="md:px-2 py-2 px-3">
      <table className="max-w-[120px]">
        <tbody>
          {data.map((item, index) => {
            const { label, value, percentage } = item
            const totalBarWidthPx = 120
            const barWidthPx = Math.max(2, (percentage * totalBarWidthPx) / 100)
            return (
              <tr key={index}>
                <td className="relative min-w-[10px] w-[10px]">
                  <div className="absolute top-0 right-0 bottom-0 left-0">
                    {index > 0 && (
                      <div className="absolute w-[1px] top-0 bottom-1/2 left-1/2 -translate-x-1/2 bg-[#E6E6E6E6]" />
                    )}
                    {index < data.length - 1 && (
                      <div className="absolute w-[1px] top-1/2 bottom-0 left-1/2 -translate-x-1/2 bg-[#E6E6E6E6]" />
                    )}
                    <div className="bg-[#196E82] w-[9px] h-[9px] rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                  </div>
                </td>

                {/* Location and brand count */}
                <td className="pl-2 pr-3 py-1 whitespace-nowrap">
                  <p className="text-xs text-[#888581]">{label} Locations</p>
                  <p className="text-sm text-[#4A4340]">
                    {value} brand{value > 1 ? 's' : ''}
                  </p>
                </td>

                {/* Bar and percentage */}
                <td className="py-1 max-w-[120px]">
                  <div className="flex items-center gap-2">
                    {/* Bar container with fixed width */}
                    <div className="max-w-[120px] min-w-[60px] relative h-[5px] bg-[#E6E6E6]">
                      {/* Bar fill */}
                      <div
                        className="absolute top-0 left-0 h-full"
                        style={{
                          width: `${barWidthPx}px`,
                          background: '#196E82',
                          opacity: Math.max(0.3, percentage / 100)
                        }}
                      />
                    </div>

                    {/* Percentage value with consistent width and right alignment */}
                    <div className="w-[45px] text-right">
                      <p className="text-xs text-black">
                        {formatNumber(percentage, { maxFractionDigits: 2 })}%
                      </p>
                    </div>
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
